/**
 * Infiltration Run — HTML string builders (intro + result + shared helpers).
 * Pure: returns HTML strings only, no DOM binding (that lives in the screen module).
 */

import { t } from '../i18n/index.js';
import { formatNumber } from '../utils/format.js';
import { CAREER_TIERS, careerNameKey, careerRank, finalSuccessChance } from '../game/index.js';
import type { InfiltrationStats, InfiltrationRunState, InfiltrationState, InfilStatType } from '../game/index.js';

/** Stat display order + CSS colour class. */
export const STAT_META: { key: keyof InfiltrationStats; cls: string }[] = [
  { key: 'disguise', cls: 'stat-disguise' },
  { key: 'combat', cls: 'stat-combat' },
  { key: 'hacking', cls: 'stat-hacking' },
  { key: 'charisma', cls: 'stat-charisma' },
];

/** Detection colour band: green < 40, yellow < 70, red otherwise. */
export function detectionBand(pct: number): 'low' | 'mid' | 'high' {
  if (pct < 40) return 'low';
  if (pct < 70) return 'mid';
  return 'high';
}

/** i18n label for a stat / 'none'. */
export function statLabel(statType: InfilStatType): string {
  return statType === 'none' ? t('infil_stat_none') : t(`infil_stat_${statType}`);
}

/** Top-fixed detection gauge. `pct` sets the initial fill width. */
export function detectionGaugeHtml(pct: number): string {
  const band = detectionBand(pct);
  const critical = pct >= 80 ? ' is-critical' : '';
  return `
    <div class="infil-gauge${critical}">
      <div class="infil-gauge-head">
        <span class="infil-gauge-label">${t('infil_detection')}</span>
        <span class="infil-gauge-val band-${band}" id="infil-gauge-val">${Math.round(pct)}%</span>
      </div>
      <div class="infil-gauge-track">
        <div class="infil-gauge-fill band-${band}" id="infil-gauge-fill" style="width:${pct}%"></div>
      </div>
    </div>`;
}

/** Four stat chips. */
export function statChipsHtml(stats: InfiltrationStats): string {
  return `<div class="infil-stats">${STAT_META.map(
    (m) => `<div class="infil-stat-chip ${m.cls}"><span class="infil-stat-dot"></span>` +
      `<span class="infil-stat-name">${t(`infil_stat_${String(m.key)}`)}</span>` +
      `<span class="infil-stat-num">${stats[m.key]}</span></div>`,
  ).join('')}</div>`;
}

/** Career ladder (low -> high) with the player's best tier highlighted. */
export function careerLadderHtml(highest: string | null): string {
  const best = careerRank(highest);
  return `<div class="infil-ladder">${CAREER_TIERS.map((tier, i) => {
    const on = best >= 0 && i <= best ? 'reached' : '';
    const cur = i === best ? 'current' : '';
    return `<div class="infil-ladder-step tier-${i} ${on} ${cur}"><span>${t(careerNameKey(tier))}</span></div>`;
  }).reverse().join('')}</div>`;
}

/** Career badge chip. */
export function careerBadgeHtml(tier: string | null): string {
  if (!tier) return `<span class="infil-career-badge tier-none">${t('infil_career_none')}</span>`;
  return `<span class="infil-career-badge tier-${careerRank(tier)}">${t(careerNameKey(tier))}</span>`;
}

/** Intro / lobby screen. */
export function introScreenHtml(inf: InfiltrationState, cooldownLeftMs: number): string {
  const onCooldown = cooldownLeftMs > 0;
  const tutorial = !inf.tutorialDone;
  const btn = onCooldown
    ? `<button class="infil-start-btn" id="infil-start" disabled>${t('infil_cooldown')} <span id="infil-cooldown">${Math.ceil(cooldownLeftMs / 1000)}s</span></button>`
    : `<button class="infil-start-btn" id="infil-start">${tutorial ? t('infil_start_tutorial') : t('infil_start')}</button>`;
  return `<div class="infil-screen"><div class="infil-intro">
      <div class="infil-eyebrow">${t('infil_eyebrow')}</div>
      <div class="infil-title">${t('infil_title')}</div>
      <div class="infil-subtitle">${t('infil_subtitle')}</div>
      <div class="infil-illust infil-illust-intro has-img" style="background-image:url('images/infil-patrol.jpg')"></div>
      ${tutorial ? `<div class="infil-tutorial-note">${t('infil_tutorial_note')}</div>` : ''}
      <div class="infil-panel">
        <div class="infil-panel-label">${t('infil_agent_stats')}</div>
        ${statChipsHtml(inf.baseStats)}
      </div>
      <div class="infil-panel">
        <div class="infil-panel-row"><span>${t('infil_highest_career')}</span>${careerBadgeHtml(inf.highestCareer)}</div>
        <div class="infil-panel-row"><span>${t('infil_total_runs')}</span><b>${inf.totalRuns}</b></div>
        <div class="infil-panel-row"><span>${t('infil_total_success')}</span><b>${inf.totalSuccess}</b></div>
        ${careerLadderHtml(inf.highestCareer)}
      </div>
      <div class="infil-hint">${t('infil_tradeoff_hint')}</div>
      ${btn}
    </div></div>`;
}

/** Result screen (success / fail big banner + rewards + retry). */
export function resultScreenHtml(run: InfiltrationRunState): string {
  const success = run.finalOutcome === 'success';
  const chance = finalSuccessChance(run.detection);
  const rewards = success
    ? `<div class="infil-reward-grid">
        <div class="infil-reward"><span>IP</span><b>+${formatNumber(run.rewardIp)}</b></div>
        <div class="infil-reward"><span>${t('infil_docs')}</span><b>+${run.rewardDocs}</b></div>
        <div class="infil-reward"><span>${t('infil_cleared')}</span><b>${run.successfulChoices}</b></div>
      </div>
      <div class="infil-result-career">
        <div class="infil-result-career-label">${t('infil_career_earned')}</div>
        ${careerBadgeHtml(run.earnedCareer)}
      </div>`
    : `<div class="infil-fail-note">${t('infil_fail_note')}</div>`;
  const retry = !success && !run.retryUsed
    ? `<button class="infil-retry-btn" id="infil-retry">${t('infil_retry_ad')}</button>`
    : '';
  return `<div class="infil-screen"><div class="infil-result ${success ? 'win' : 'lose'}">
      <div class="infil-result-banner">${success ? t('infil_result_success') : t('infil_result_fail')}</div>
      <div class="infil-result-sub">${success ? t('infil_result_success_sub') : t('infil_result_fail_sub')}</div>
      <div class="infil-result-detection band-${detectionBand(run.detection)}">
        ${t('infil_final_chance')}: <b>${chance}%</b>
      </div>
      ${rewards}
      ${retry}
      <button class="infil-close-btn" id="infil-close">${t('infil_close')}</button>
    </div></div>`;
}
